import { useParams, useNavigate } from 'react-router-dom';
import { useState, Fragment, useContext, useEffect } from 'react';
import { useGet } from '@/query/fetchHooks';
import { InputText, FilterDropdown, TEOLoader } from '@teo/components';
import { Search } from '@teo/components/icons';
import { T } from '@/components/T';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Userwithorganisationwithidps } from '@/models';
import { useMobile } from '@/util';
import { NavContext } from '@/components/NavContext';
import i18n from 'i18next';
import { DataTable } from '@/components/DataTable/DataTable';
import OptionsAction from '@/components/DataTable/OptionsAction';
import { format } from 'date-fns';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';

function FollowLearningPaths({ title }: { courseType: string; title: string }) {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;
  const navigate = useNavigate();
  const { organisationId } = useParams();

  const [filter, setFilter] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [listCategories, setListCategories] = useState<any>([]);
  const [category, setCategory] = useState<any>();
  const [loader, setLoader] = useState(true);
  const { setSubPage } = useContext(NavContext);

  const basePath = window.location.pathname;

  const allCategories: any = useGet(`/categories`, {
    count: 1000,
    sort: 'tag',
    lang: auth?.user?.lang,
    organisationId: `${auth?.user?.activeOrganisationId},OR,organisationId=null`,
  });

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  useEffect(() => {
    const myFilter = filterInput + ' ' + (category?.name ? category?.name : '');
    setFilter(myFilter);
  }, [filterInput, category]);

  useEffect(() => {
    if (allCategories?.data) {
      const data = (allCategories?.data?.data as any[]).map((tag) => ({
        id: tag?.id,
        name: tag?.tag,
      }));
      setListCategories(data);
    }
  }, [allCategories?.data]);

  return (
    <div className="w-full max-w-6xl pb-8">
      {isMobile ? (
        <MobileSubPageMenu title={title} />
      ) : (
        <div className="my-8 flex justify-between">
          <h1 className="text-xl font-semibold text-black">
            <T>{t(title)}</T>
          </h1>
        </div>
      )}
      <Fragment>
        <div className="pb-8 sm:w-2/3 md:w-1/2">
          <InputText
            value={filterInput}
            onChange={(ev) => setFilterInput(ev.target.value)}
            Icon={Search}
            placeholder={t('search.learningpath')}
          />
        </div>
        <div className="mb-8 flex flex-col sm:flex-row">
          <span className="mb-4 pr-2 sm:mb-0">
            <FilterDropdown
              clearButtonLabel="clear"
              onChange={(value: any) => {
                setCategory(value);
              }}
              buttonText={`${t('pages.follow_up.all_categories_tag')}`}
              options={listCategories}
              searchable={true}
              value={category}
            />
          </span>
        </div>
        {loader && (
          <div className="mx-auto flex w-full justify-center py-20 px-10">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        )}
        <div className={`w-full ${loader === true && ' hidden '}`}>
          <DataTable
            screensUpdateTable="mobile"
            filter={filter}
            fetch={useGet.bind(null, '/myusedlps', {
              organisationId: authUser?.activeOrganisationId,
              userId: authUser?.id,
              onSuccess: (result: any) => {
                setLoader(false);
              },
              onError: (res: any) => {
                console.error('res', res);
                setLoader(false);
              },
            })}
            empty={{
              template: 2,
              text: t('pages.follow_learningpath.lp_list_empty'),
            }}
            defaultSort={[
              { id: '-updatedAt', desc: false },
              { id: 'lpName', desc: false },
            ]}
            rowPath={(learningpaths: any) => `${basePath}/${learningpaths.id}`}
            columns={[
              {
                title: t('columns.learningpath_name'),
                id: 'lpName',
                className: 'col-start-1 col-end-7 flex items-center',
                classNameDesktop: 'max-w-[300px] w-[65%]',
                render: (learningpaths: any) => (
                  <div className="flex items-center">
                    <div className="mr-3 w-16 min-w-16 sm:w-20 sm:min-w-20">
                      <img
                        src={
                          learningpaths?.lpPicture ||
                          '/assets/images/LearnPath.png'
                        }
                        alt={learningpaths?.lpName}
                        className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                      />
                    </div>
                    {learningpaths?.lpName}
                  </div>
                ),
              },
              {
                addTitleOnMobile: true,
                className:
                  'col-start-1 col-end-4 row-start-2 flex items-center',
                id: 'createdAt',
                title: t('columns.created'),
                render: (learningpaths: any) =>
                  format(new Date(learningpaths.createdAt), 'dd/MM/yyyy'),
              },
              {
                className:
                  'col-start-9 row-start-1 justify-end flex items-center',
                id: 'actionButton',
                title: '',
                sortable: false,
                render: (learningpaths: any) => (
                  <>
                    <OptionsAction
                      menuItems={[
                        {
                          addIcon: true,
                          id: '1',
                          title: t('pages.follow_learningpath.go_to_career_lp'),
                          onClick: (ev) => {
                            navigate(
                              `/${i18n.language}/create/${organisationId}/learning-paths/${learningpaths?.id}`
                            );
                          },
                        },
                      ]}
                      pageDetail={{
                        title: t('pages.follow_learningpath.go_to_career_lp'),
                        rowPath: (learningpaths) =>
                          `${basePath}/${learningpaths.id}`,
                      }}
                      screensUpdateTable="mobile"
                      rowInfo={learningpaths}
                      mobileHeader={{
                        render: (learningpaths: any) => (
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <div className="mr-3 w-16 min-w-16 sm:w-20 sm:min-w-20">
                              <img
                                src={
                                  learningpaths.lpPicture ||
                                  '/assets/images/LearnPath.png'
                                }
                                alt={learningpaths.lpName}
                                className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                              />
                            </div>
                            {learningpaths.lpName}
                          </div>
                        ),
                      }}
                    />
                  </>
                ),
              },
            ]}
          />
        </div>
      </Fragment>
    </div>
  );
}

export default FollowLearningPaths;
